import { Component, ElementRef, OnDestroy, OnInit, QueryList, ViewChildren } from '@angular/core';
import { Router } from '@angular/router';
import { toolKitList, ToolkitItem } from './../constants/toolkitSideBar.constant';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CommonCommunicationService } from '../core/services/commonCommunication.service';
import { CommonUtilityService } from '../core/services/commonUtility.service';
import { TranslationService } from '../core/services/translation.service';
import { currentReadOnlySession, READ_ONLY_BANNER_TEXT } from '../core/utils/read-only-session';

@Component({
  selector: 'app-layout',
  templateUrl: './layout.component.html',
  styleUrls: ['./layout.component.scss'],
  standalone: false
})
export class LayoutComponent implements OnInit, OnDestroy {
  toolKitList: ToolkitItem[] = [];
  activePanel: string | null = null; // Tracks which panel is currently open
  panelClosing: boolean = false;
  isSidebarCollapsed: boolean = false;
  expandedPanel: boolean = false; // Tracks if panel is in full-width mode
  private destroy$ = new Subject<void>();
  // Suspended-store banner (token claim readOnly:true). Display only — the backend
  // enforces. null (no banner) for every token without the claim.
  readOnlyBanner: string | null = null;

  @ViewChildren('toolkitIcon') toolkitIcons!: QueryList<ElementRef>;

  constructor(
    private ccs: CommonCommunicationService,
    private commonUtility: CommonUtilityService,
    private translatePipe: TranslationService,
    private router: Router
  ) {
    this.toolKitList = toolKitList;
    this.ccs.isSidebarCollapsed$.pipe(takeUntil(this.destroy$)).subscribe((state) => {
      this.isSidebarCollapsed = state;
    });
  }

  ngOnInit(): void {
    this.readOnlyBanner = currentReadOnlySession() ? READ_ONLY_BANNER_TEXT : null;
    // Check if any toolkit should open by default
    const defaultPanel = this.toolKitList.find((t) => t.openByDefault && t.panelComponent);
    if (defaultPanel) {
      this.activePanel = defaultPanel.id;
      this.expandedPanel = defaultPanel.expandByDefault || false;
    }
  }

  layoutClicked() {
    this.ccs.setNotificationBarState('CLOSE');
    this.ccs.setUserBarState('CLOSE');
  }

  toggleSidebar() {
    this.ccs.toggleSidebar();
  }

  navigateTo(toolkit: ToolkitItem) {
    if (toolkit?.route) {
      if (this.activePanel) this.closePanel();
      this.router.navigateByUrl(toolkit.route);
    } else if (toolkit?.panelComponent) {
      // Toggle panel - close if same panel clicked, otherwise switch to new panel
      if (this.activePanel === toolkit.id) {
        this.closePanel();
      } else {
        this.activePanel = toolkit.id;
        this.expandedPanel = toolkit.expandByDefault || false;
      }
    } else {
      let msg = this.translatePipe.translateMethod('ui_message_something_went_lbl');
      this.commonUtility.showDanger(msg);
    }
  }

  closePanel() {
    this.panelClosing = true;
    setTimeout(() => {
      this.activePanel = null;
      this.panelClosing = false;
      this.expandedPanel = false;
    }, 300);
  }

  onPanelClose(event: boolean) {
    if (!event) {
      this.closePanel();
    }
  }

  getActiveToolkit(): ToolkitItem | undefined {
    return this.toolKitList.find((t) => t.id === this.activePanel);
  }

  getTriggerElement(toolkitId: string): ElementRef | undefined {
    const index = this.toolKitList.findIndex((t) => t.id === toolkitId);
    return this.toolkitIcons?.toArray()[index];
  }

  getPanelWidth(): number {
    if (this.expandedPanel) {
      // Calculate full width minus sidebar (220px or 50px when collapsed) minus toolkit sidebar (40px)
      const sidebarWidth = this.isSidebarCollapsed ? 50 : 220;
      return window.innerWidth - sidebarWidth - 40;
    }
    const activeToolkit = this.getActiveToolkit();
    return activeToolkit?.panelWidth || 400;
  }

  onExpandToggle(isExpanded: boolean) {
    this.expandedPanel = isExpanded;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
